import { Grid, IconButton, InputAdornment, Stack } from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import ActionButton from 'common/components/ActionButton';
import CustomTextValidator from 'common/components/CustomTextValidators';
import CustomTimeRangePicker from 'common/components/CustomTimeRangePicker';
import useFlexBoxStyles from 'common/flexBox';
import { useIncludeDateValidation } from 'common/hooks/useIncludeDateValidation';
import LayoutForm from 'common/layouts/LayoutForm';
import { useFormFields } from 'libs/hooksLib';
import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import { ValidatorForm } from 'react-material-ui-form-validator';
import { useDispatch } from 'react-redux';
import { closeNestedDialogAction } from 'redux/slices/nestedDialogSlice';
import { CalendarToday } from '@mui/icons-material';
import { useState } from 'react';

const transformDates = (dates) => {
    return dates.map((dateRange) => ({
        startDate: dateRange[0],
        endDate: dateRange[1],
    }));
};

const DateOverrideItem = (props) => {
    const { onSubmit } = props;
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { classes: flexBox, cx } = useFlexBoxStyles();
    const { isValidHourRange, error, validationType, setError } = useIncludeDateValidation();

    const [fields, handleFieldChange] = useFormFields({
        date: '',
        overrides: [],
    });

    const [open, setOpen] = useState(false);

    const handleEditDate = (newDate, idx) => {
        const newRange = [
            ...fields.overrides.slice(0, idx),
            [newDate.start, newDate.end],
            ...fields.overrides.slice(idx + 1),
        ];
        saveRange(newRange);
    };

    const handleAddDate = (newDate) => {
        const newRange = [...fields.overrides, [newDate.start, newDate.end]];
        saveRange(newRange);
    };

    const handleDeleteDate = (idx) => {
        deleteError(idx);
        const newRange = [...fields.overrides.slice(0, idx), ...fields.overrides.slice(idx + 1)];
        saveRange(newRange);
    };

    const deleteError = (idx) => {
        if (error?.idx === idx) {
            setError(null);
        }
    };

    const saveRange = (data) => {
        handleFieldChange({
            target: {
                name: 'overrides',
                value: data,
            },
        });
    };

    const onClose = () => {
        dispatch(closeNestedDialogAction());
    };

    const handleSubmit = () => {
        onSubmit({ ...fields }, () => onClose());
    };

    const handleChange = (date, idx, callback) => {
        const ranges = transformDates(fields.overrides);
        const isValid = isValidHourRange({ ranges, date, idx, type: validationType.UNSELECTED });
        if (isValid) {
            if (idx === -1) {
                handleAddDate(date);
                callback?.();
            } else {
                handleEditDate(date, idx);
            }
        }
    };

    return (
        <LayoutForm
            size="small"
            title={t('calendar.addADateOverride').toUpperCase()}
            // customClasses={flexBox.vH70}
            // confirmDlg={{
            //     open: openCancelDlg,
            //     accept: finalCancel,
            //     cancel: cancelDlg,
            //     titleDlg: confirmTitle,
            //     messageDlg: confirmMessage,
            // }}
        >
            <Stack direction="column" spacing={2} className={cx(flexBox.overflowHidden, flexBox.h100)}>
                <Stack direction="column" spacing={3} className={cx(flexBox.overflowHidden, flexBox.h100)}>
                    <ValidatorForm
                        onSubmit={() => {}}
                        onError={() => {}}
                        className={cx(flexBox.flex, flexBox.gap2, flexBox.column, flexBox.overflowHidden, flexBox.h100)}
                    >
                        <Stack
                            direction="column"
                            className={cx(flexBox.overflowXHidden, flexBox.scrollbarNone, flexBox.h100)}
                        >
                            <Grid container xs={12}>
                                <Grid item xs={4} alignItems="center" className={flexBox.paddingRight1}>
                                    <LocalizationProvider dateAdapter={AdapterMoment}>
                                        <DesktopDatePicker 
                                            value={fields.date ? moment(fields.date, 'YYYY-MM-DD') : null}
                                            onChange={(newValue) =>
                                                handleFieldChange({
                                                    target: { name: 'date', value: moment(newValue).format('YYYY-MM-DD') },
                                                })
                                            }
                                            open={open}
                                            onOpen={() => setOpen(true)}
                                            onClose={() => setOpen(false)}
                                            PaperProps={
                                                {
                                                    style: { borderRadius: '16px' },
                                                }
                                            }
                                            renderInput={(params) => (
                                                <CustomTextValidator
                                                    {...params}
                                                    inputProps={{
                                                        ...params.inputProps,
                                                        readOnly: true,
                                                    }}
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                            <InputAdornment position="end" sx={{ mr: 1 }}>
                                                                <IconButton onClick={() => setOpen(true)} size="small">
                                                                    <CalendarToday sx={{ fontSize: 16 }} />
                                                                </IconButton>
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    onClick={() => setOpen(true)}
                                                />
                                            )}
                                        />
                                    </LocalizationProvider>
                                </Grid>
                                <Grid item xs={8}>
                                    <Stack direction="column" spacing={1}>
                                        {fields.overrides?.map((date, idx) => (
                                            <Stack direction="column" key={idx}>
                                                <CustomTimeRangePicker
                                                    date={{ start: date[0], end: date[1] }}
                                                    onApply={(date) => handleChange(date, idx)}
                                                    onDelete={() => handleDeleteDate(idx)}
                                                    error={error?.idx === idx ? error?.msg : null}
                                                    disabled={error ? error?.idx !== idx : false}
                                                    dates={transformDates(fields.overrides)}
                                                />
                                            </Stack>
                                        ))}

                                        <CustomTimeRangePicker
                                            onApply={(date) => handleChange(date, -1)}
                                            error={error?.idx === -1 ? error?.msg : null}
                                            disabled={error ? error?.idx !== -1 : !fields.date}
                                            dates={transformDates(fields.overrides)}
                                        />
                                    </Stack>
                                </Grid>
                            </Grid>
                        </Stack>
                    </ValidatorForm>
                </Stack>
                <Stack
                    direction="row"
                    justifyContent="flex-end"
                    alignItems="center"
                    spacing={1}
                    className={flexBox.paddingTop2}
                >
                    <ActionButton onClick={onClose} color="default" fitWidth>
                        {t('app.common.cancel')}
                    </ActionButton>

                    <ActionButton
                        color="primary"
                        autoFocus
                        fitWidth
                        disabled={fields.overrides.length === 0 || error}
                        onClick={handleSubmit}
                    >
                        {t('app.common.save')}
                    </ActionButton>
                </Stack>
            </Stack>
        </LayoutForm>
    );
};

export default DateOverrideItem;
